import Loader from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { DateFilter } from "components/Filters/DateFilter";
import { Funnel } from "components/icons/Funnel";
import { NO_ASSGINEE_KEY, NO_ASSIGNEE } from "constants/agent";
import {
  EXCLUDE_INKLE_ADMIN_MESSAGES,
  EXCLUDE_SYSTEM_MESSAGES,
} from "constants/chatFilter";
import { ALL_CHAT_FILTER, UNREAD_CHAT_FILTER } from "constants/chatOtherFilter";
import { OTHER_FILTER_STARRED_CHAT } from "types/contants/crm";
import { NOT_STARTED, NOT_STARTED_KEY } from "constants/taskStatus";
import { FilterValues } from "contexts/ChatFilterContext";
import { useUsEntities } from "hooks/useUsEntities";
import { FilterCheckboxList } from "components/chat/FilterCheckboxList";
import { useChatSeasons } from "hooks/useChatSeasons";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import React from "react";
import { useGetAssignAgentsQuery } from "store/apis/agents";
import { useLazyGetAllGroupsQuery } from "store/apis/crm";
import { useGetAllBaseTaskTemplatesQuery } from "store/apis/task";
import { useGetAllTaskStatesQuery } from "store/apis/taskTags";
import { CompanyGroup } from "types/Models/groups";
import { debounce } from "utils/debouncing";

type ChannelListFilterProps = {
  values: FilterValues;
  updateFilter: <S extends keyof FilterValues>(
    name: S,
    newValue: FilterValues[S]
  ) => void;
};

export type FilterTypes =
  | "CHANNEL"
  | "ENTITY"
  | "STATUS"
  | "COMPANY"
  | "AGENT"
  | "YEAR"
  | "LAST_MESSAGE_DATE"
  | "OTHERS";

const LoaderState = () => {
  return (
    <div className="t-flex t-items-center t-justify-center t-h-full">
      <Loader customType="secondary" />
    </div>
  );
};

const StatusList = ({ updateFilter, values }: ChannelListFilterProps) => {
  const {
    data: taskStatesData = [],
    isLoading,
    isSuccess,
  } = useGetAllTaskStatesQuery();

  const taskStates = [
    ...taskStatesData,
    { name: NOT_STARTED, uuid: NOT_STARTED_KEY },
  ];

  return (
    <Async.Root
      isLoading={isLoading}
      isEmpty={taskStates.length === 0}
      isSuccess={isSuccess}
      customLoader={<LoaderState />}
    >
      <Async.Empty>No results found</Async.Empty>
      <Async.Success>
        <MultiSelectFilter
          onChange={(value) => {
            updateFilter("STATUS", value);
          }}
          options={taskStates.map(({ name, uuid }) => ({
            value: uuid,
            label: name,
          }))}
          selected={values.STATUS}
        />
      </Async.Success>
    </Async.Root>
  );
};

const EntityList = ({ updateFilter, values }: ChannelListFilterProps) => {
  const entities = useUsEntities();

  const handleChange = (selected: string[] | null) =>
    updateFilter("ENTITY", selected);

  return (
    <FilterCheckboxList
      allLabel="All entities"
      onChange={handleChange}
      options={entities.map(({ uuid, name }) => ({
        value: uuid,
        label: name,
      }))}
      selected={values.ENTITY}
    />
  );
};

const YearList = ({
  seasons,
  updateFilter,
  values,
}: ChannelListFilterProps & { seasons: string[] }) => {
  const handleChange = (selected: string[] | null) =>
    updateFilter("SEASON", selected);

  return (
    <FilterCheckboxList
      allLabel="All Years"
      onChange={handleChange}
      options={seasons.map((season) => ({ value: season, label: season }))}
      selected={values.SEASON}
    />
  );
};

const ChannelList = ({ updateFilter, values }: ChannelListFilterProps) => {
  const [searchTerm, setSearchTerm] = React.useState<null | string>(null);
  const { data: baseTaskTemplates = [], isLoading } =
    useGetAllBaseTaskTemplatesQuery({
      searchString: searchTerm,
    });

  const baseTaskTemplatesWithOutBooks = baseTaskTemplates.filter(
    ({ task_category_type }) => task_category_type !== "BOOKS"
  );

  const options = [
    ...baseTaskTemplatesWithOutBooks,
    { title: "Books", uuid: "BOOKS" },
    { title: "General Help", uuid: "GENERAL_HELP" },
  ];

  const handleSearch = debounce((value: string) => {
    setSearchTerm(value || null);
  });

  if (isLoading) {
    return <LoaderState />;
  }

  return (
    <MultiSelectFilter
      onSearchValueChange={handleSearch}
      onChange={(value) => {
        updateFilter("CHANNEL", value);
      }}
      options={options.map(({ title, uuid }) => ({
        value: uuid,
        label: title,
      }))}
      selected={values.CHANNEL}
    />
  );
};

const AgentList = ({ updateFilter, values }: ChannelListFilterProps) => {
  const { isAdmin } = useRoleBasedView();
  const {
    data: serviceTeam = [],
    isLoading,
    isSuccess,
  } = useGetAssignAgentsQuery({}, { skip: !isAdmin });
  const agents = [
    ...serviceTeam,
    { name: NO_ASSIGNEE, profile_id: NO_ASSGINEE_KEY },
  ];

  return (
    <Async.Root
      isLoading={isLoading}
      isEmpty={serviceTeam.length === 0}
      isSuccess={isSuccess}
      customLoader={<LoaderState />}
    >
      <Async.Empty>No results found</Async.Empty>
      <Async.Success>
        <MultiSelectFilter
          onChange={(value) => {
            updateFilter("AGENT", value);
          }}
          options={agents.map(({ name, profile_id }) => ({
            value: profile_id,
            label: name,
          }))}
          selected={values.AGENT}
        />
      </Async.Success>
    </Async.Root>
  );
};

const CompanyList = ({ updateFilter, values }: ChannelListFilterProps) => {
  const [searchTerm, setSearchTerm] = React.useState<null | string>(null);
  const {
    data: groups,
    loadNext,
    isLoading,
    isSuccess,
  } = usePaginatedQuery<{
    company_groups?: CompanyGroup[];
  }>(useLazyGetAllGroupsQuery, "company_groups", {
    search_term: searchTerm,
  });

  const handleSearch = debounce((value: string) => {
    setSearchTerm(value || null);
  });

  return (
    <Async.Root
      isLoading={isLoading}
      isEmpty={false}
      isSuccess={isSuccess}
      customLoader={<LoaderState />}
    >
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        <MultiSelectFilter
          onSearchValueChange={handleSearch}
          hasMore={true}
          totalCount={groups?.company_groups?.length}
          loadNext={loadNext}
          onChange={(value) => updateFilter("COMPANY", value)}
          options={
            groups?.company_groups?.map((group) => ({
              value: group.uuid,
              label: group.name,
            })) || []
          }
          selected={values.COMPANY}
        />
      </Async.Success>
    </Async.Root>
  );
};

const FilterListItem = ({
  value,
  hideFilters,
  children,
}: {
  value: FilterTypes;
  hideFilters: FilterTypes[];
  children: React.ReactNode;
}) => {
  if (hideFilters?.includes(value)) {
    return null;
  }

  return <Filter.ListItem value={value}>{children}</Filter.ListItem>;
};

export const ChannelListFilter = ({
  updateFilter,
  values,
  toggleSortByUnread,
  hideFilters = [],
}: ChannelListFilterProps & {
  toggleSortByUnread: () => void;
  hideFilters?: FilterTypes[];
}) => {
  const query = useQuery();
  const { update } = useUpdateQuery();
  const entities = useUsEntities();
  const seasons = useChatSeasons();
  const { uuid: groupId } = useCurrentGroup();

  const otherRadioValue = query.get("other") || null;

  const handleOtherRadioChange = (value?: string) => {
    if (otherRadioValue === value) {
      update({ query: "other", value: null });
    } else {
      update({ query: "other", value: value });
    }
  };

  const isChecked = otherRadioValue === OTHER_FILTER_STARRED_CHAT;

  const handleUnreadChange = (value: FilterValues["OTHERS"]) => {
    updateFilter("OTHERS", value);
    toggleSortByUnread();
  };

  const handleStarredChange = () =>
    handleOtherRadioChange(isChecked ? "" : OTHER_FILTER_STARRED_CHAT);

  const handlePendingActionChange = () =>
    updateFilter("IS_PENDING_ACTION", !values.IS_PENDING_ACTION);

  const toggleExclude = (option: string) => {
    const isExcluded = values.EXCLUDE.includes(option);
    const newExclude = isExcluded
      ? values.EXCLUDE.filter((excluded) => excluded !== option)
      : [...values.EXCLUDE, option];

    updateFilter("EXCLUDE", newExclude);
  };

  const hasEntities = entities.length > 0;
  const hasSeasons = seasons.length > 0;
  const isGroupChat = Boolean(groupId);

  return (
    <Filter.Root
      defaultValue="CHANNEL"
      trigger={
        <Button customType="icon" size="xsmall">
          <Funnel />
        </Button>
      }
    >
      <Filter.Portal>
        <Filter.List>
          {hasEntities && (
            <FilterListItem hideFilters={hideFilters} value="ENTITY">
              Entities
            </FilterListItem>
          )}
          <FilterListItem hideFilters={hideFilters} value="CHANNEL">
            Channel
          </FilterListItem>
          <FilterListItem hideFilters={hideFilters} value="STATUS">
            Status
          </FilterListItem>
          <FilterListItem hideFilters={hideFilters} value="COMPANY">
            Company
          </FilterListItem>
          <FilterListItem hideFilters={hideFilters} value="AGENT">
            Agent
          </FilterListItem>
          {hasSeasons && (
            <FilterListItem hideFilters={hideFilters} value="YEAR">
              Year
            </FilterListItem>
          )}
          <FilterListItem hideFilters={hideFilters} value="LAST_MESSAGE_DATE">
            Last message date
          </FilterListItem>
          <FilterListItem hideFilters={hideFilters} value="OTHERS">
            Others
          </FilterListItem>
        </Filter.List>
        <Filter.Body block value="ENTITY">
          <EntityList updateFilter={updateFilter} values={values} />
        </Filter.Body>
        <Filter.Body block value="CHANNEL">
          <ChannelList updateFilter={updateFilter} values={values} />
        </Filter.Body>
        <Filter.Body block value="STATUS">
          <StatusList updateFilter={updateFilter} values={values} />
        </Filter.Body>
        <Filter.Body block value="COMPANY">
          <CompanyList updateFilter={updateFilter} values={values} />
        </Filter.Body>
        <Filter.Body block value="AGENT">
          <AgentList updateFilter={updateFilter} values={values} />
        </Filter.Body>
        <Filter.Body block value="YEAR">
          <YearList
            seasons={seasons}
            updateFilter={updateFilter}
            values={values}
          />
        </Filter.Body>
        <Filter.Body block value="LAST_MESSAGE_DATE">
          <DateFilter values={values} updateFilter={updateFilter} />
        </Filter.Body>
        <Filter.Body block value="OTHERS">
          <div className="t-flex t-flex-col t-gap-3">
            <Radio.Root
              onValueChange={handleUnreadChange}
              value={values.OTHERS}
            >
              <Radio.Content>
                <Radio.Item value={ALL_CHAT_FILTER}>All chat</Radio.Item>
                <Radio.Item value={UNREAD_CHAT_FILTER}>Unread chat</Radio.Item>
              </Radio.Content>
            </Radio.Root>
            {isGroupChat && (
              <Checkbox
                label="Chat with pending actions"
                name="pendingActionsChat"
                onChange={handlePendingActionChange}
                checked={values.IS_PENDING_ACTION}
              />
            )}
            <Checkbox
              label="Starred chat"
              name="starredChat"
              onChange={handleStarredChange}
              checked={isChecked}
            />

            <div className="t-border-0 t-border-t t-border-solid t-border-neutral-10" />

            <div className="t-text-subtext-sm t-uppercase t-tracking-wide t-text-text-30">
              Message type
            </div>
            <Checkbox
              label="Exclude system messages"
              name="excludeSystemMessages"
              onChange={() => toggleExclude(EXCLUDE_SYSTEM_MESSAGES)}
              checked={values.EXCLUDE.includes(EXCLUDE_SYSTEM_MESSAGES)}
            />
            <Checkbox
              label="Exclude Inkle Admin messages"
              name="excludeInkleAdminMessages"
              onChange={() => toggleExclude(EXCLUDE_INKLE_ADMIN_MESSAGES)}
              checked={values.EXCLUDE.includes(EXCLUDE_INKLE_ADMIN_MESSAGES)}
            />
          </div>
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
